// /deposit (docs/08-app-design.md 5.4, 5.7): "Add funds", organised by intent.
// One card with two tabs, "Receive to your vault" (the vault's own address, or
// its wSOL account, one at a time, each with its QR) and "From a wallet" (the
// Wallet Standard send); under the tabs one shared "I sent it" arrival check.
// Below the card sits its own "Network fees" section: the fee reserve panel,
// funding it from a wallet, and, behind a disclosure, "send out". Nothing
// here ever asks for a one-time key: a wallet only ever signs a plain SOL
// transfer, never a vault instruction. Presentation only: the polling, the
// fee reserve panel and the send-out form are the ones this screen always had.
// On a legacy device whose older fee reserve can be moved now, a line under
// the fee reserve panel (not inside the closed "Manage the fee reserve") says
// so and links to the overview's notice, where the move is reviewed
// (docs/18-one-fee-reserve.md section 6).

import { address } from '@solana/kit';
import { WSOL_MINT, WSOL_MINT_ADDRESS, addressBytes, ataAddress } from '@thevault/sdk';
import { TOKEN_PROGRAM } from '@thevault/sdk/canary';
import { type KeyboardEvent, useCallback, useEffect, useId, useRef, useState } from 'react';
import { GasTankPanel, useGasTank } from '../components/GasTank';
import { OlderReservePointer, movableReading, useOlderReserve } from '../components/OlderReserve';
import { Address, Amount, Button, Field, Kv, Notice, Qr, Sheet, StatusLine, Stepper, Toast, useToast } from '../components/primitives';
import { Shell } from '../components/Shell';
import { SceneCard } from '../components/SceneWindow';
import { WalletSend, solStringToLamports } from '../components/Wallet';
import { WRONG_NETWORK_SENTENCE } from '../deployment';
import { type Engine, describe } from '../engine';
import { type Rpc, toBase58 } from '../rpc';
import { session, useSession } from '../session';
import { KNOWN, WSOL_MINT_B58 } from '../tokens';
import { explorerBase, explorerUrl, shortSig } from '../util';
import { sendOutCheck } from '@thevault/sdk';

const TOKEN_PROGRAM_ADDRESS = 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA';
const TOKEN_2022_ADDRESS = 'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb';
/** "I sent it" polls every 3 s for up to 3 minutes (docs/08 5.4). */
const POLL_INTERVAL_MS = 3_000;
const POLL_TIMEOUT_MS = 180_000;

// --------------------------------------------------------------- polling

interface DepositSnapshot {
  lamports: bigint;
  tokens: Record<string, { mint: string; amount: bigint }>;
}

async function snapshotDeposits(rpc: Rpc, vaultAddress: string): Promise<DepositSnapshot> {
  // The token index is optional here: an RPC that refuses it without a key still lets native deposits be noticed.
  const [account, rows] = await Promise.all([
    rpc.getAccountInfo(vaultAddress),
    Promise.all([rpc.getTokenAccountsByOwner(vaultAddress, TOKEN_PROGRAM_ADDRESS), rpc.getTokenAccountsByOwner(vaultAddress, TOKEN_2022_ADDRESS)])
      .then((r) => r.flat())
      .catch(() => []),
  ]);
  const tokens: Record<string, { mint: string; amount: bigint }> = {};
  for (const r of rows) tokens[r.pubkey] = { mint: r.mint, amount: r.amount };
  return { lamports: account?.lamports ?? 0n, tokens };
}

interface Arrival {
  raw: bigint;
  decimals: number;
  symbol: string;
}

/** What changed between two snapshots, in the form Amount can show; native lamports take priority since that is what "I sent it" is usually waiting for. */
async function describeArrival(rpc: Rpc, baseline: DepositSnapshot, now: DepositSnapshot): Promise<Arrival | null> {
  if (now.lamports > baseline.lamports) return { raw: now.lamports - baseline.lamports, decimals: 9, symbol: 'SOL' };
  for (const [pubkey, cur] of Object.entries(now.tokens)) {
    const before = baseline.tokens[pubkey]?.amount ?? 0n;
    if (cur.amount > before) {
      let decimals = 0;
      try {
        decimals = await rpc.getMintDecimals(cur.mint);
      } catch {
        /* shown in raw units */
      }
      const symbol = cur.mint === WSOL_MINT_ADDRESS ? 'SOL' : `${cur.mint.slice(0, 4)}…${cur.mint.slice(-4)}`;
      return { raw: cur.amount - before, decimals, symbol };
    }
  }
  return null;
}

type PollState = { kind: 'idle' } | { kind: 'polling' } | { kind: 'landed'; arrival: Arrival } | { kind: 'timeout' };

/** "I sent it" (docs/08 5.4): poll the chain every 3 s for up to 3 minutes for anything new under the vault's address, in either token program. */
function useDepositPoll(rpc: Rpc, vaultAddress: string) {
  const [state, setState] = useState<PollState>({ kind: 'idle' });
  const stopRef = useRef(false);
  const runningRef = useRef(false);
  useEffect(
    () => () => {
      stopRef.current = true;
    },
    [],
  );
  const start = useCallback(() => {
    if (runningRef.current) return;
    runningRef.current = true;
    stopRef.current = false;
    setState({ kind: 'polling' });
    void (async () => {
      try {
        const baseline = await snapshotDeposits(rpc, vaultAddress);
        const startedAt = Date.now();
        while (!stopRef.current && Date.now() - startedAt < POLL_TIMEOUT_MS) {
          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          if (stopRef.current) return;
          try {
            const now = await snapshotDeposits(rpc, vaultAddress);
            const arrival = await describeArrival(rpc, baseline, now);
            if (arrival) {
              if (!stopRef.current) setState({ kind: 'landed', arrival });
              return;
            }
          } catch {
            /* the shim or the RPC hiccuped; try again next tick */
          }
        }
        if (!stopRef.current) setState({ kind: 'timeout' });
      } finally {
        runningRef.current = false;
      }
    })();
  }, [rpc, vaultAddress]);
  return { state, start };
}

// ----------------------------------------------------------- send out

/**
 * Send out (docs/08 5.7; docs/18-one-fee-reserve.md 5.1 A, the Send out row, and M18): the fee payer is checked after
 * the fee and again at the end, so the reserve must hold 0 or at least its account minimum F at both points. The form
 * shows the engine's limits from a live read (everything but the fee, which empties the reserve to 0, or at most
 * B − fee − F) and refuses anything else; at Confirm the engine reads the reserve and the destination again and refuses
 * before signing (a destination below F must end with at least F).
 */
function SendOutForm({ engine, txBase, onSent }: { engine: Engine; txBase: string | null; onSent: () => void }) {
  const [rule, setRule] = useState<Awaited<ReturnType<Engine['sendOutRule']>> | null>(null);
  const [destination, setDestination] = useState('');
  const [amount, setAmount] = useState('');
  const [everything, setEverything] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [state, setState] = useState<'idle' | 'sending' | 'landed' | 'failed'>('idle');
  const [signature, setSignature] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void engine
      .sendOutRule()
      .then((r) => {
        if (!cancelled) setRule(r);
      })
      .catch((e) => {
        if (!cancelled) setError(describe(e));
      });
    return () => {
      cancelled = true;
    };
  }, [engine, state]);

  const planned: bigint | null = everything ? (rule?.everything ?? null) : solStringToLamports(amount);
  // The payer rule on the form's own read; the destination is read at Confirm.
  const check = rule && planned !== null ? sendOutCheck({ balance: rule.balance, minimum: rule.minimum, amount: planned, fee: rule.fee }) : null;
  const canSubmit = destination.trim().length > 0 && planned !== null && planned > 0n && !!check?.ok;
  const nothing = rule !== null && rule.everything === null;

  const send = async (): Promise<boolean> => {
    setState('sending');
    setError(null);
    try {
      if (!everything && (planned === null || planned <= 0n)) throw new Error('Nothing to send.');
      const destBytes = addressBytes(address(destination.trim()));
      const result = await engine.sendGas(destBytes, everything ? 'everything' : planned!);
      setSignature(result.signature);
      setState('landed');
      onSent();
      return true;
    } catch (e) {
      setError(describe(e));
      setState('failed');
      return false;
    }
  };

  return (
    <div className="stack tight">
      <Field id="send-out-destination" label="Destination address">
        <input id="send-out-destination" className="input" value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="Solana address" autoComplete="off" />
      </Field>
      <Field id="send-out-amount" label="Amount (SOL)">
        <input id="send-out-amount" className="input" inputMode="decimal" value={amount} disabled={everything} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
      </Field>
      <label className="checkbox-row">
        <input type="checkbox" checked={everything} onChange={(e) => setEverything(e.target.checked)} />
        <span>Everything but the fee</span>
      </label>
      {nothing && rule ? (
        <StatusLine state="failed" word="Nothing to send out.">
          The fee reserve holds <Amount raw={rule.balance} decimals={9} symbol="SOL" maxFraction={9} />. Sending anything out needs at least <Amount raw={rule.minimum + rule.fee} decimals={9} symbol="SOL" maxFraction={9} />: its account minimum plus the fee.
        </StatusLine>
      ) : rule ? (
        <p className="activity-note">
          The fee reserve keeps its account minimum of <Amount raw={rule.minimum} decimals={9} symbol="SOL" maxFraction={9} /> unless it is emptied: send at most{' '}
          {rule.maxPartial !== null ? <Amount raw={rule.maxPartial} decimals={9} symbol="SOL" maxFraction={9} /> : 'nothing short of everything'}, or everything but the fee
          {rule.everything !== null ? (
            <>
              {' '}
              (<Amount raw={rule.everything} decimals={9} symbol="SOL" maxFraction={9} />), which empties it
            </>
          ) : null}
          .
        </p>
      ) : null}
      {!nothing && check && !check.ok && planned !== null && planned > 0n ? (
        <StatusLine state="failed" word="Not allowed.">
          {check.reason === 'leaves-below-minimum'
            ? 'That would leave the fee reserve below its account minimum. Send less, or everything but the fee.'
            : check.reason === 'over-balance'
              ? 'That is more than the fee reserve holds after the fee.'
              : 'Nothing can be sent out of the fee reserve now.'}
        </StatusLine>
      ) : null}
      <div className="actions">
        <Button small onClick={() => setConfirmOpen(true)} disabled={!canSubmit}>
          Send out
        </Button>
      </div>
      {state === 'landed' && signature ? (
        <StatusLine state="landed" word="Landed.">
          Transaction{' '}
          {explorerUrl(txBase, signature) ? (
            <a href={explorerUrl(txBase, signature)!} target="_blank" rel="noreferrer">
              {shortSig(signature)} ↗
            </a>
          ) : (
            shortSig(signature)
          )}
          .
        </StatusLine>
      ) : null}
      {state === 'failed' ? (
        <StatusLine state="failed" word="Not completed.">
          {error}
        </StatusLine>
      ) : null}
      <Sheet open={confirmOpen} onClose={() => (state === 'sending' ? undefined : setConfirmOpen(false))} title="Send out">
        <h2>Send out.</h2>
        <p>The fee reserve pays network fees and rent only. It never holds vault funds.</p>
        <Kv rows={[['Destination', destination || '—'], ['Amount', everything ? 'Everything but the fee' : amount ? `${amount} SOL` : '—']]} />
        {everything ? <p className="ink">This empties the fee reserve for every device that uses this vault.</p> : null}
        {state === 'failed' ? (
          <StatusLine state="failed" word="Not completed.">
            {error}
          </StatusLine>
        ) : null}
        <div className="actions">
          <Button
            onClick={() =>
              void send().then((ok) => {
                if (ok) setConfirmOpen(false);
              })
            }
            disabled={state === 'sending'}
            working={state === 'sending'}
          >
            {state === 'sending' ? 'Sending' : 'Confirm'}
          </Button>
          <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={state === 'sending'}>
            Cancel
          </Button>
        </div>
      </Sheet>
    </div>
  );
}


// --------------------------------------------------------------- page

type Tab = 'receive' | 'wallet';
type Destination = 'sol' | 'wsol';
/**
 * What the fee reserve panel's next step is (docs/18 5.1 A; R78): before activation, Activate, whose requirement
 * (`activationCost()`, F + f + K + the vault's rent shortfall) is read with the chain state; afterwards an ordinary action
 * (one plain action). Read again when a deposit lands, since a deposit before activation changes the shortfall. A read
 * that fails gives no figure ("Working out what the next step needs…"), never the plain action's smaller one.
 */
function useNextStep(engine: Engine, depositLanded: boolean): { kind: 'reading' } | { kind: 'activation'; need: bigint } | { kind: 'active' } {
  const [step, setStep] = useState<{ kind: 'reading' } | { kind: 'activation'; need: bigint } | { kind: 'active' }>({ kind: 'reading' });
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const state = await engine.readChain();
        const next = state.kind === 'absent' || state.kind === 'prefunded' ? { kind: 'activation' as const, need: (await engine.activationCost(state)).total } : { kind: 'active' as const };
        if (!cancelled) setStep(next);
      } catch {
        if (!cancelled) setStep({ kind: 'reading' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [engine, depositLanded]);
  return step;
}

const TAB_ORDER: Tab[] = ['receive', 'wallet'];
const TAB_LABELS: Record<Tab, string> = { receive: 'Receive to your vault', wallet: 'From a wallet' };

export function Deposit() {
  const { settings } = useSession();
  const engine = session.engine();
  const [wsolAta, setWsolAta] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('receive');
  const [destination, setDestination] = useState<Destination>('sol');
  const [toast, say] = useToast();
  const depositPoll = useDepositPoll(engine.rpc, engine.info.address);
  // the engine's own figures for the panel (docs/18 5.1 A): what the next step needs, and the ten-action allowance
  // F + K + 10 × 15,000 as a labelled suggestion. Before activation (an absent or prefunded vault: deposits may arrive
  // first) the next step is Activate, so the figure is activationCost(), as on /vault; afterwards it is one plain action.
  // Its network check (docs/18 2.5) also gates "Send from a wallet" to the reserve below.
  const { tank: reserve, network: reserveNetwork } = useGasTank(engine);
  const nextStep = useNextStep(engine, depositPoll.state.kind === 'landed');
  // This device's older fee reserve, when the table can move it now (docs/18 section 6); the move itself is on the overview.
  const movable = movableReading(useOlderReserve(engine));
  const needed = nextStep.kind === 'activation' ? nextStep.need : nextStep.kind === 'active' ? (reserve?.plainAction ?? null) : null;
  const base = useId();
  const feesHeadingId = useId();
  const tabButtons = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    let cancelled = false;
    void ataAddress(engine.vaultAddressBytes, WSOL_MINT, TOKEN_PROGRAM).then((bytes) => {
      if (!cancelled) setWsolAta(toBase58(bytes));
    });
    return () => {
      cancelled = true;
    };
  }, [engine]);

  const addrBase = explorerBase(settings.rpcUrl, 'address');
  const txBase = explorerBase(settings.rpcUrl, 'tx');
  const explorer = addrBase ? explorerUrl(addrBase, '')! : undefined;

  const pollKind = depositPoll.state.kind;
  const pollWord = pollKind === 'landed' ? 'Landed.' : 'Nothing yet.';
  const pollLineState = pollKind === 'landed' ? 'landed' : pollKind === 'timeout' ? 'neutral' : 'pending';

  const selectTab = (next: Tab, focus = false) => {
    setTab(next);
    if (focus) tabButtons.current[TAB_ORDER.indexOf(next)]?.focus();
  };
  // Arrow keys, Home and End move between the tabs (the tab pattern's own keyboard model).
  const onTabKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = TAB_ORDER.indexOf(tab);
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      selectTab(TAB_ORDER[(i + (e.key === 'ArrowRight' ? 1 : TAB_ORDER.length - 1)) % TAB_ORDER.length]!, true);
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      selectTab(e.key === 'Home' ? TAB_ORDER[0]! : TAB_ORDER[TAB_ORDER.length - 1]!, true);
    }
  };

  // The side steps follow the check and claim nothing it has not found: send, then check, then what the check saw.
  const stepStates: Array<'todo' | 'active' | 'done'> = pollKind === 'polling' ? ['done', 'active', 'todo'] : pollKind === 'landed' ? ['done', 'done', 'done'] : ['active', 'todo', 'todo'];

  // SOL's monogram comes from the known-token table (neutral if the entry were ever absent).
  const sol = KNOWN[WSOL_MINT_B58];
  const glyphClass = `token-glyph ${sol ? `t-${sol.symbol.toLowerCase()}` : ''}`.trim();

  return (
    <Shell number="03" title="Deposit" detail="TO THE VAULT AND THE FEE RESERVE">
      <div className="task">
        <div className="task-main">
          <div className="composer-head">
            <h1>Add funds</h1>
            <span className="dim">TO YOUR VAULT</span>
          </div>

          <section className="composer" aria-label="Add funds to your vault">
            <div className="segmented" role="tablist" aria-label="How to add funds" onKeyDown={onTabKey}>
              {TAB_ORDER.map((t, i) => (
                <button
                  key={t}
                  ref={(el) => {
                    tabButtons.current[i] = el;
                  }}
                  type="button"
                  role="tab"
                  id={`${base}-tab-${t}`}
                  aria-selected={tab === t}
                  aria-controls={`${base}-panel-${t}`}
                  tabIndex={tab === t ? 0 : -1}
                  onClick={() => selectTab(t)}
                >
                  {TAB_LABELS[t]}
                </button>
              ))}
            </div>

            <div role="tabpanel" id={`${base}-panel-receive`} aria-labelledby={`${base}-tab-receive`} hidden={tab !== 'receive'}>
              <div className="stack">
                <div className="segmented" role="group" aria-label="Which account to receive into">
                  <button type="button" aria-pressed={destination === 'sol'} onClick={() => setDestination('sol')}>
                    SOL
                  </button>
                  <button type="button" aria-pressed={destination === 'wsol'} onClick={() => setDestination('wsol')}>
                    Wrapped SOL
                  </button>
                </div>
                <div className="side-row">
                  <span>
                    <span className={glyphClass} aria-hidden="true">
                      S
                    </span>
                    {destination === 'sol' ? 'SOL, to your vault address' : 'Wrapped SOL, to your vault’s wSOL account'}
                  </span>
                </div>
                <p className="ink">
                  {destination === 'sol'
                    ? 'SOL sent to your vault address arrives as native SOL. To swap it, choose Make swappable on the Overview.'
                    : 'Wrapped SOL sent to the vault’s wSOL account is swappable at once.'}
                </p>
                {destination === 'sol' ? (
                  <div className="address-block">
                    <Qr text={engine.info.address} label="QR code of the vault address" />
                    <div className="stack tight">
                      <Address value={engine.info.address} kind="vault" full explorer={explorer} onCopied={() => say('Copied')} />
                    </div>
                  </div>
                ) : wsolAta ? (
                  <div className="address-block">
                    <Qr text={wsolAta} label="QR code of the vault wSOL account address" />
                    <div className="stack tight">
                      <Address value={wsolAta} kind="token account" full explorer={explorer} onCopied={() => say('Copied')} />
                    </div>
                  </div>
                ) : (
                  <p>Working out the account address…</p>
                )}
              </div>
            </div>

            <div role="tabpanel" id={`${base}-panel-wallet`} aria-labelledby={`${base}-tab-wallet`} hidden={tab !== 'wallet'}>
              <div className="stack">
                <p className="ink">Send SOL from a browser wallet straight to your vault address. The wallet signs a plain transfer and nothing else.</p>
                <WalletSend destination={engine.info.address} rpc={engine.rpc} rpcUrl={settings.rpcUrl} onSent={() => depositPoll.start()} />
              </div>
            </div>

            <div className="stack tight">
              <div className="side-row">
                <span>Sent something? Check for arrival.</span>
                <Button small onClick={() => depositPoll.start()} disabled={pollKind === 'polling'} working={pollKind === 'polling'}>
                  {pollKind === 'polling' ? 'Checking' : 'I sent it'}
                </Button>
              </div>
              <p className="activity-note">The check looks for new funds under your vault for up to 3 minutes. It reports only what it finds and never confirms a transfer by itself.</p>
              {depositPoll.state.kind !== 'idle' ? (
                <StatusLine state={pollLineState} word={pollWord}>
                  {depositPoll.state.kind === 'landed' ? (
                    <>
                      <Amount raw={depositPoll.state.arrival.raw} decimals={depositPoll.state.arrival.decimals} symbol={depositPoll.state.arrival.symbol} /> arrived.
                    </>
                  ) : depositPoll.state.kind === 'timeout' ? (
                    'Checked every 3 seconds for 3 minutes. Send it, then check again.'
                  ) : (
                    'Checking every 3 seconds.'
                  )}
                </StatusLine>
              ) : null}
            </div>
          </section>

          <section className="stack" aria-labelledby={feesHeadingId}>
            <div className="composer-head">
              <h2 id={feesHeadingId}>Network fees</h2>
            </div>
            <p>A separate fee reserve pays network fees and account rent for what you do in your vault. It is funded on its own: sending to your vault address does not fund it.</p>
            <GasTankPanel engine={engine} needed={needed} suggested={reserve?.suggested ?? null} />
            {/* in view, not inside the closed "Manage the fee reserve" (docs/18 section 6: the panel points to the move) */}
            {movable ? <OlderReservePointer reading={movable} /> : null}
            <Notice label="Send from a wallet">
              {/* a wallet send to the reserve only once the network check has passed (docs/18 2.5) */}
              {reserveNetwork === 'ok' ? (
                <>
                  <p className="ink">Fund the fee reserve the same way, from any wallet.</p>
                  <div className="actions">
                    <WalletSend destination={engine.relayerAddress} rpc={engine.rpc} rpcUrl={settings.rpcUrl} onSent={() => {}} />
                  </div>
                </>
              ) : reserveNetwork === 'wrong' ? (
                <p className="ink">{WRONG_NETWORK_SENTENCE}</p>
              ) : (
                <p className="ink">Checking the network before a wallet can fund the fee reserve…</p>
              )}
            </Notice>
            <details>
              <summary className="eyebrow dim">Manage the fee reserve</summary>
              <div className="stack tight">
                <span className="eyebrow">Send out</span>
                <p className="ink">Move SOL out of the fee reserve. It pays network fees and rent only, and every device that opens this vault uses it.</p>
                <SendOutForm engine={engine} txBase={txBase} onSent={() => {}} />
              </div>
            </details>
          </section>
        </div>

        <aside className="task-side" aria-label="How adding funds works">
          <SceneCard scene="deposit" />
          <div className="panel">
            <span className="eyebrow">What happens</span>
            <Stepper
              steps={[
                { title: 'Send', detail: 'From any wallet, to the address you chose.', state: stepStates[0]! },
                { title: 'Check for arrival', detail: 'Press I sent it. The app looks for new funds for up to 3 minutes.', state: stepStates[1]! },
                { title: 'See it in your vault', detail: 'Funds that arrive show on your overview.', state: stepStates[2]! },
              ]}
            />
          </div>
          <div className="panel">
            <span className="eyebrow">Good to know</span>
            <p>Deposits can arrive before the vault is activated. They wait at the address until you act.</p>
            <p>The fee reserve needs SOL for any action, activation included. Fund it under Network fees.</p>
          </div>
        </aside>
      </div>

      <Toast message={toast} />
    </Shell>
  );
}
